import { useState } from "react";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useRatingsReport } from "@/hooks/useRatingsReport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Star,
  TrendingUp,
  TrendingDown,
  Users,
  MessageSquare,
  Download,
  Calendar,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";

export default function AvaliacoesPage() {
  const { activeRestaurant } = useRestaurantContext();
  const { ratings, stats, isLoading } = useRatingsReport(activeRestaurant?.id);

  const formatDate = (date: string) => {
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-muted-foreground">-</span>;
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating ? "fill-yellow-400 text-yellow-400" : "text-muted"
            }`}
          />
        ))}
      </div>
    );
  };

  const getNpsBadge = (nps: number) => {
    if (nps >= 50) return <Badge className="bg-green-500">Excelente</Badge>;
    if (nps >= 0) return <Badge className="bg-yellow-500">Regular</Badge>;
    return <Badge variant="destructive">Crítico</Badge>;
  };

  const exportToExcel = () => {
    const data = ratings.map((r) => ({
      Data: formatDate(r.created_at),
      Mesa: r.table_number || "-",
      Cliente: r.customer_name || "-",
      Email: r.customer_email || "-",
      Telefone: r.customer_phone || "-",
      "Qualidade Comida": r.food_quality || "-",
      "Qualidade Serviço": r.service_quality || "-",
      Ambiente: r.environment || "-",
      "Custo-Benefício": r.value_for_money || "-",
      "Experiência Geral": r.overall_experience || "-",
      Comentário: r.comments || "-",
      "Aceita Marketing": r.accept_marketing ? "Sim" : "Não",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Avaliações");
    XLSX.writeFile(wb, `avaliacoes-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Avaliações dos Clientes</h1>
          <p className="text-muted-foreground">
            Acompanhe a satisfação dos clientes pelo tablet
          </p>
        </div>
        <Button onClick={exportToExcel} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Exportar Excel
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Avaliações
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRatings}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">NPS</CardTitle>
            {stats.nps >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{stats.nps}</span>
              {getNpsBadge(stats.nps)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.promoters} promotores • {stats.detractors} detratores
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Experiência Média</CardTitle>
            <Star className="h-4 w-4 text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {stats.avgOverallExperience.toFixed(1)}
              </span>
              <span className="text-muted-foreground">/5</span>
            </div>
            {renderStars(Math.round(stats.avgOverallExperience))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Com Comentários</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {ratings.filter((r) => r.comments).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {((ratings.filter((r) => r.comments).length / stats.totalRatings) * 100 || 0).toFixed(0)}% das avaliações
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Category Averages */}
      <Card>
        <CardHeader>
          <CardTitle>Médias por Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Qualidade da Comida</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">
                  {stats.avgFoodQuality.toFixed(1)}
                </span>
                {renderStars(Math.round(stats.avgFoodQuality))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Atendimento</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">
                  {stats.avgServiceQuality.toFixed(1)}
                </span>
                {renderStars(Math.round(stats.avgServiceQuality))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Ambiente</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">
                  {stats.avgEnvironment.toFixed(1)}
                </span>
                {renderStars(Math.round(stats.avgEnvironment))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Custo-Benefício</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">
                  {stats.avgValueForMoney.toFixed(1)}
                </span>
                {renderStars(Math.round(stats.avgValueForMoney))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ratings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliações Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {ratings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma avaliação registrada ainda.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Mesa</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Experiência</TableHead>
                  <TableHead>Comentário</TableHead>
                  <TableHead>Marketing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ratings.slice(0, 50).map((rating) => (
                  <TableRow key={rating.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(rating.created_at)}
                    </TableCell>
                    <TableCell>{rating.table_number || "-"}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{rating.customer_name || "-"}</p>
                        {rating.customer_phone && (
                          <p className="text-xs text-muted-foreground">
                            {rating.customer_phone}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {renderStars(rating.overall_experience)}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {rating.comments || (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {rating.accept_marketing ? (
                        <Badge variant="secondary">Sim</Badge>
                      ) : (
                        <span className="text-muted-foreground">Não</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
